import { createEdge, createNode } from './workflowEngine'

export const workflowCategories = [
  { id: 'all', label: 'All' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'professional_services', label: 'Professional services' },
  { id: 'home_services', label: 'Home services' },
  { id: 'real_estate', label: 'Real estate' },
  { id: 'general_business', label: 'General business' },
  { id: 'beauty_wellness', label: 'Beauty & wellness' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'hospitality', label: 'Hospitality' },
  { id: 'retail', label: 'Retail' },
]

const DEFAULT_BRANCHES = [
  { id: 'yes', label: 'Yes', keywords: ['yes', 'ready', 'book', 'continue'] },
  { id: 'no', label: 'No', keywords: ['no', 'later', 'not now', 'not sure'] },
]

const PRESET_DEFAULTS = {
  booking: {
    greeting: 'Hello, thanks for calling.',
    messageText: 'I will collect a few details and help you book the right time.',
    collectTitle: 'Collect the details',
    collectFields: ['name', 'phone', 'service', 'date', 'time'],
    requiredFields: ['name', 'phone', 'service'],
    decisionTitle: 'Ready to book?',
    yes: {
      kind: 'booking',
      title: 'Book appointment',
      description: 'Create or confirm the appointment.',
      config: { service: 'Consultation', duration: 30, calendar: 'Main calendar', confirmationChannel: 'sms' },
      followUp: {
        kind: 'contact',
        title: 'Send confirmation',
        description: 'Send the booking confirmation.',
        config: { channel: 'sms', template: 'Your appointment is confirmed.' },
      },
    },
    no: {
      kind: 'knowledge',
      title: 'Answer common questions',
      description: 'Answer FAQs or route to the right person.',
      config: { baseName: 'Knowledge base', fallbackMessage: 'I can pass this to the team if needed.' },
      followUp: {
        kind: 'transfer',
        title: 'Route to staff',
        description: 'Transfer if the question needs a human.',
        config: { target: 'Front desk', announce: 'I will connect you with the front desk.' },
      },
    },
  },
  intake: {
    greeting: 'Hello, thanks for reaching out.',
    messageText: 'I will collect the basics and route this to the right next step.',
    collectTitle: 'Collect intake details',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['name', 'phone'],
    decisionTitle: 'Do we have enough detail?',
    yes: {
      kind: 'transfer',
      title: 'Route to the team',
      description: 'Pass the request to the right person.',
      config: { target: 'Intake team', announce: 'I will route this to the intake team.' },
    },
    no: {
      kind: 'contact',
      title: 'Request more info',
      description: 'Send a follow-up to collect the missing details.',
      config: { channel: 'sms', template: 'Please reply with the missing details so we can continue.' },
    },
  },
  support: {
    greeting: 'Hello, thanks for contacting us.',
    messageText: 'I will try to answer the question or escalate it if needed.',
    collectTitle: 'Capture the issue',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['name', 'phone'],
    decisionTitle: 'Can we resolve this now?',
    yes: {
      kind: 'knowledge',
      title: 'Answer from knowledge base',
      description: 'Use the knowledge base to answer the request.',
      config: { baseName: 'Support knowledge base', fallbackMessage: 'I can check this for you.' },
      followUp: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Send a recap or next step to the customer.',
        config: { channel: 'email', subject: 'Support follow-up', template: 'We are reviewing your request.' },
      },
    },
    no: {
      kind: 'transfer',
      title: 'Escalate to human',
      description: 'Transfer to the right team member.',
      config: { target: 'Support team', announce: 'I will transfer you to support.' },
    },
  },
  payment: {
    greeting: 'Hello, thanks for the update.',
    messageText: 'I can confirm the request and send a secure payment step if needed.',
    collectTitle: 'Collect payment details',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['name', 'phone', 'service'],
    decisionTitle: 'Ready for payment?',
    yes: {
      kind: 'payment',
      title: 'Request payment',
      description: 'Send a deposit or full payment link.',
      config: { mode: 'deposit', amount: 50, percentage: 20, currency: 'USD', message: 'I can send the payment link now.' },
      followUp: {
        kind: 'contact',
        title: 'Send payment confirmation',
        description: 'Confirm the payment request by message.',
        config: { channel: 'sms', template: 'Your payment link has been sent.' },
      },
    },
    no: {
      kind: 'booking',
      title: 'Continue to booking',
      description: 'Continue with the booking once the missing details are collected.',
      config: { service: 'Consultation', duration: 30, calendar: 'Main calendar', confirmationChannel: 'sms' },
    },
  },
  handoff: {
    greeting: 'Hello, thanks for calling.',
    messageText: 'I will collect the key details and route you to the right person if needed.',
    collectTitle: 'Collect the request',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['name', 'phone'],
    decisionTitle: 'Should a human take over?',
    yes: {
      kind: 'transfer',
      title: 'Transfer to human',
      description: 'Route the call to a person or team.',
      config: { target: 'Team member', announce: 'I will connect you with a person now.' },
    },
    no: {
      kind: 'knowledge',
      title: 'Answer from knowledge base',
      description: 'Answer the request with documents or policies.',
      config: { baseName: 'Knowledge base', fallbackMessage: 'I can check that for you.' },
    },
  },
  followup: {
    greeting: 'Hello, thanks for your time.',
    messageText: 'I will send the right follow-up message and keep the customer informed.',
    collectTitle: 'Collect follow-up details',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['name', 'phone'],
    decisionTitle: 'Send the follow-up now?',
    yes: {
      kind: 'contact',
      title: 'Send follow-up',
      description: 'Send a reminder, recap, or status update.',
      config: { channel: 'sms', template: 'Thanks for your time. We will follow up shortly.' },
    },
    no: {
      kind: 'end',
      title: 'Close the flow',
      description: 'End the workflow without sending anything yet.',
      config: { closing: 'Thanks, we will be in touch.' },
    },
  },
  knowledge: {
    greeting: 'Hello, thanks for calling.',
    messageText: 'I will answer from the knowledge base or route it if the answer is not available.',
    collectTitle: 'Capture the question',
    collectFields: ['name', 'phone', 'service', 'notes'],
    requiredFields: ['service'],
    decisionTitle: 'Can the knowledge base answer this?',
    yes: {
      kind: 'knowledge',
      title: 'Answer from knowledge base',
      description: 'Use documents, policies, or pricing info.',
      config: { baseName: 'Knowledge base', fallbackMessage: 'I can check that for you.' },
    },
    no: {
      kind: 'transfer',
      title: 'Escalate to human',
      description: 'Pass the request to a person.',
      config: { target: 'Human agent', announce: 'I will transfer you to a person.' },
    },
  },
  callback: {
    greeting: 'Hello, thanks for calling.',
    messageText: 'I will capture the reason for the callback and route it properly.',
    collectTitle: 'Capture callback details',
    collectFields: ['name', 'phone', 'service', 'preferred_time'],
    requiredFields: ['name', 'phone'],
    decisionTitle: 'Do we have enough for a callback?',
    yes: {
      kind: 'contact',
      title: 'Send callback confirmation',
      description: 'Send a callback confirmation or summary.',
      config: { channel: 'sms', template: 'Thanks, we will call you back shortly.' },
    },
    no: {
      kind: 'transfer',
      title: 'Route to staff',
      description: 'Transfer to the right team member.',
      config: { target: 'Reception', announce: 'I will connect you with the team.' },
    },
  },
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function createMeta(id, categoryId, title, description, icon, tags = [], featured = false, preset = 'booking', flow = {}) {
  return {
    id,
    key: id,
    categoryId,
    category: categoryId,
    title,
    name: title,
    description,
    icon,
    tags,
    featured,
    preset,
    flow,
    status: 'draft',
    version: 1,
    settings: {
      snapToGrid: true,
      gridSize: 20,
      autoFit: true,
    },
  }
}

function mergeFlowConfig(def) {
  const preset = PRESET_DEFAULTS[def.preset] || PRESET_DEFAULTS.booking
  return {
    greeting: def.flow?.greeting || preset.greeting,
    messageText: def.flow?.messageText || preset.messageText,
    collectTitle: def.flow?.collectTitle || preset.collectTitle,
    collectDescription: def.flow?.collectDescription || 'Collect the information needed to move forward.',
    collectFields: def.flow?.collectFields || preset.collectFields,
    requiredFields: def.flow?.requiredFields || preset.requiredFields,
    collectPrompt: def.flow?.collectPrompt || 'Please share the details we need to continue.',
    decisionTitle: def.flow?.decisionTitle || preset.decisionTitle,
    decisionDescription: def.flow?.decisionDescription || 'Choose the best branch for this request.',
    branches: def.flow?.branches || clone(DEFAULT_BRANCHES),
    yes: {
      ...(clone(preset.yes || {})),
      ...(clone(def.flow?.yes || {})),
    },
    no: {
      ...(clone(preset.no || {})),
      ...(clone(def.flow?.no || {})),
    },
    endTitle: def.flow?.endTitle || 'End conversation',
    endDescription: def.flow?.endDescription || 'Close the interaction cleanly.',
    endClosing: def.flow?.endClosing || 'Thanks, goodbye.',
  }
}

function buildTemplateWorkflow(def, agentId) {
  const flow = mergeFlowConfig(def)
  const start = createNode('start', { x: 360, y: 40 }, {
    id: 'start',
    title: 'Start conversation',
    description: 'Open the call and greet the caller.',
    config: { greeting: flow.greeting, language: 'english' },
  })
  const message = createNode('message', { x: 340, y: 210 }, {
    id: 'message',
    title: def.messageTitle || 'Set context',
    description: def.messageDescription || 'Explain what the agent will handle next.',
    config: { text: flow.messageText },
  })
  const collect = createNode('collect', { x: 340, y: 390 }, {
    id: 'collect',
    title: flow.collectTitle,
    description: flow.collectDescription,
    config: {
      fields: flow.collectFields,
      requiredFields: flow.requiredFields,
      prompt: flow.collectPrompt,
    },
  })
  const decision = createNode('decision', { x: 340, y: 570 }, {
    id: 'decision',
    title: flow.decisionTitle,
    description: flow.decisionDescription,
    config: {
      branches: flow.branches,
      fallbackLabel: 'Ask for more details',
    },
  })

  const yesNode = createNode(flow.yes.kind || 'booking', { x: 60, y: 780 }, {
    id: 'yes',
    title: flow.yes.title || 'Continue',
    description: flow.yes.description || '',
    config: flow.yes.config || {},
  })
  const noNode = createNode(flow.no.kind || 'transfer', { x: 620, y: 780 }, {
    id: 'no',
    title: flow.no.title || 'Fallback',
    description: flow.no.description || '',
    config: flow.no.config || {},
  })

  const yesFollowUp = flow.yes.followUp
    ? createNode(flow.yes.followUp.kind || 'contact', { x: 60, y: 980 }, {
        id: 'yes_follow',
        title: flow.yes.followUp.title || 'Follow up',
        description: flow.yes.followUp.description || '',
        config: flow.yes.followUp.config || {},
      })
    : null

  const noFollowUp = flow.no.followUp
    ? createNode(flow.no.followUp.kind || 'contact', { x: 620, y: 980 }, {
        id: 'no_follow',
        title: flow.no.followUp.title || 'Follow up',
        description: flow.no.followUp.description || '',
        config: flow.no.followUp.config || {},
      })
    : null

  const endY = Math.max(
    980,
    yesFollowUp ? 1180 : 0,
    noFollowUp ? 1180 : 0
  )

  const end = createNode('end', { x: 360, y: endY }, {
    id: 'end',
    title: flow.endTitle,
    description: flow.endDescription,
    config: { closing: flow.endClosing },
  })

  const nodes = [start, message, collect, decision, yesNode, noNode]
  if (yesFollowUp) nodes.push(yesFollowUp)
  if (noFollowUp) nodes.push(noFollowUp)
  nodes.push(end)

  const edges = [
    createEdge('start', 'message', { sourceHandle: 'out', targetHandle: 'in', label: 'Next' }),
    createEdge('message', 'collect', { sourceHandle: 'out', targetHandle: 'in', label: 'Next' }),
    createEdge('collect', 'decision', { sourceHandle: 'out', targetHandle: 'in', label: 'Next' }),
    createEdge('decision', 'yes', { sourceHandle: 'yes', targetHandle: 'in', label: flow.branches?.[0]?.label || 'Yes' }),
    createEdge('decision', 'no', { sourceHandle: 'no', targetHandle: 'in', label: flow.branches?.[1]?.label || 'No' }),
    createEdge('yes', yesFollowUp ? 'yes_follow' : 'end', { sourceHandle: 'out', targetHandle: 'in', label: yesFollowUp ? 'Confirm' : 'Finish' }),
    createEdge('no', noFollowUp ? 'no_follow' : 'end', { sourceHandle: 'out', targetHandle: 'in', label: noFollowUp ? 'Follow up' : 'Finish' }),
  ]

  if (yesFollowUp) {
    edges.push(createEdge('yes_follow', 'end', { sourceHandle: 'out', targetHandle: 'in', label: 'Finish' }))
  }

  if (noFollowUp) {
    edges.push(createEdge('no_follow', 'end', { sourceHandle: 'out', targetHandle: 'in', label: 'Finish' }))
  }

  return {
    id: null,
    agent_id: agentId || null,
    name: def.title,
    description: def.description,
    status: 'draft',
    version: 1,
    nodes,
    edges,
    settings: {
      snapToGrid: true,
      gridSize: 20,
      autoFit: true,
    },
    validation_status: 'draft',
  }
}

const templateDefinitions = [
  createMeta(
    'missed_call_recovery',
    'general_business',
    'Missed-call recovery',
    'Call back missed leads, qualify the request, collect details, and route or book.',
    'phone-return',
    ['missed calls', 'lead recovery', 'revenue'],
    true,
    'callback',
    {
      messageText: 'I saw your missed call and I am calling back right away.',
      decisionTitle: 'Do we have enough to help?',
      yes: {
        kind: 'booking',
        title: 'Book the next slot',
        description: 'Offer the next available slot or callback time.',
        config: { service: 'Callback follow-up', duration: 30, calendar: 'Service calendar' },
        followUp: {
          kind: 'contact',
          title: 'Send SMS follow-up',
          description: 'Confirm the callback by text message.',
          config: { channel: 'sms', template: 'Thanks for your call. We will follow up shortly.' },
        },
      },
      no: {
        kind: 'transfer',
        title: 'Transfer to human',
        description: 'Connect the caller to the right person.',
        config: { target: 'Service desk', announce: 'I will transfer you to the team.' },
      },
    }
  ),
  createMeta(
    'appointment_booking',
    'general_business',
    'Appointment booking',
    'Collect client details, propose availability, book the appointment, and send confirmation.',
    'calendar',
    ['booking', 'confirmation', 'scheduling'],
    true,
    'booking',
    {
      messageText: 'I will collect the details needed to book your appointment.',
      yes: {
        kind: 'booking',
        title: 'Book appointment',
        description: 'Create the appointment and check the calendar.',
        config: { service: 'Appointment booking', duration: 30, calendar: 'Main calendar' },
        followUp: {
          kind: 'contact',
          title: 'Send confirmation',
          description: 'Send the booking confirmation by SMS or email.',
          config: { channel: 'sms', template: 'Your appointment is confirmed.' },
        },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer availability questions',
        description: 'Answer common scheduling questions if the client is not ready.',
        config: { baseName: 'Booking FAQ', fallbackMessage: 'I can answer a few quick questions.' },
      },
    }
  ),
  createMeta(
    'lead_qualification',
    'general_business',
    'Lead qualification',
    'Ask qualification questions, score the lead, capture contact details, and notify the team.',
    'lead',
    ['lead scoring', 'qualification', 'sales'],
    true,
    'intake',
    {
      messageText: 'I will ask a few quick questions to see how we can help.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this a strong fit?',
      yes: {
        kind: 'transfer',
        title: 'Notify sales',
        description: 'Pass the lead to the right sales person.',
        config: { target: 'Sales team', announce: 'I will connect you with sales.' },
      },
      no: {
        kind: 'contact',
        title: 'Send next steps',
        description: 'Send a helpful follow-up with the next step.',
        config: { channel: 'email', subject: 'Thanks for reaching out', template: 'We will review your request and follow up.' },
      },
    }
  ),
  createMeta(
    'customer_support_triage',
    'general_business',
    'Customer support triage',
    'Understand the request, answer common questions, escalate urgent issues, or create a ticket.',
    'support',
    ['support', 'triage', 'ticketing'],
    true,
    'support',
    {
      collectFields: ['name', 'phone', 'service', 'notes', 'order_id'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Can we resolve this now?',
      yes: {
        kind: 'knowledge',
        title: 'Answer from knowledge base',
        description: 'Use the support knowledge base to answer common issues.',
        config: { baseName: 'Support KB', fallbackMessage: 'I can check that for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Escalate to support',
        description: 'Route urgent or complex issues to the support team.',
        config: { target: 'Support team', announce: 'I will transfer you to support.' },
      },
    }
  ),
  createMeta(
    'payment_or_deposit_request',
    'general_business',
    'Payment or deposit request',
    'Confirm the service, request a deposit or payment link, and send follow-up confirmation.',
    'payment',
    ['payments', 'deposit', 'revenue'],
    true,
    'payment',
    {
      decisionTitle: 'Ready to request payment?',
      yes: {
        kind: 'payment',
        title: 'Request deposit',
        description: 'Send a deposit or payment link.',
        config: { mode: 'deposit', amount: 50, percentage: 20, currency: 'USD', message: 'I can send the payment link now.' },
        followUp: {
          kind: 'contact',
          title: 'Send payment confirmation',
          description: 'Confirm the payment request by text message.',
          config: { channel: 'sms', template: 'Your payment link has been sent.' },
        },
      },
      no: {
        kind: 'booking',
        title: 'Continue to booking',
        description: 'Continue with the booking and request payment later.',
        config: { service: 'Service booking', duration: 30, calendar: 'Main calendar' },
      },
    }
  ),
  createMeta(
    'human_handoff',
    'general_business',
    'Human handoff',
    'Detect sensitive or complex requests and transfer the conversation to the right person.',
    'handoff',
    ['handoff', 'escalation', 'human'],
    true,
    'handoff',
    {
      decisionTitle: 'Should a human take over?',
      yes: {
        kind: 'transfer',
        title: 'Transfer to human',
        description: 'Move the conversation to the right person or team.',
        config: { target: 'Team member', announce: 'I will connect you with a person now.' },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer from knowledge base',
        description: 'Answer using the knowledge base if possible.',
        config: { baseName: 'Knowledge base', fallbackMessage: 'I can check that for you.' },
      },
    }
  ),
  createMeta(
    'follow_up_automation',
    'general_business',
    'Follow-up automation',
    'Send reminders, confirmations, status updates, or post-service follow-ups.',
    'followup',
    ['follow-up', 'reminders', 'automation'],
    true,
    'followup',
    {
      collectFields: ['name', 'phone', 'service', 'notes', 'preferred_time'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Send the follow-up now?',
      yes: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Send a reminder, recap, or status update.',
        config: { channel: 'sms', template: 'Thanks for your time. We will follow up shortly.' },
      },
      no: {
        kind: 'end',
        title: 'Close the flow',
        description: 'End without sending a message yet.',
        config: { closing: 'Thanks, we will be in touch.' },
      },
    }
  ),
  createMeta(
    'knowledge_base_assistant',
    'general_business',
    'Knowledge-base assistant',
    'Answer questions using uploaded business documents, policies, pricing, and procedures.',
    'knowledge',
    ['knowledge base', 'faq', 'documents'],
    true,
    'knowledge',
    {
      collectFields: ['name', 'phone', 'service', 'question'],
      requiredFields: ['question'],
      decisionTitle: 'Can the knowledge base answer this?',
      yes: {
        kind: 'knowledge',
        title: 'Answer from knowledge base',
        description: 'Use the company knowledge base for the answer.',
        config: { baseName: 'Company knowledge base', fallbackMessage: 'I can check that for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Escalate to human',
        description: 'Send the request to a person when the answer is not available.',
        config: { target: 'Human agent', announce: 'I will transfer you to a person.' },
      },
    }
  ),
  createMeta(
    'multi_step_intake',
    'general_business',
    'Multi-step intake',
    'Collect structured information before an appointment, consultation, quote, or service request.',
    'intake',
    ['intake', 'forms', 'structured data'],
    true,
    'intake',
    {
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have enough to continue?',
      yes: {
        kind: 'booking',
        title: 'Continue to booking',
        description: 'Use the collected details to move into booking.',
        config: { service: 'Consultation', duration: 30, calendar: 'Main calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Ask for missing details',
        description: 'Collect the missing information by message.',
        config: { channel: 'sms', template: 'Please reply with the missing details so we can continue.' },
      },
    }
  ),

  createMeta(
    'dental_clinic_appointment_booking',
    'healthcare',
    'Dental clinic appointment booking',
    'Collect patient details, answer common questions, book, and confirm the appointment.',
    'tooth',
    ['booking', 'patient intake', 'revenue'],
    false,
    'booking',
    {
      greeting: 'Hello, thank you for calling the dental clinic.',
      messageText: 'I will collect a few details and help book your visit.',
      collectTitle: 'Collect patient details',
      collectFields: ['name', 'phone', 'service', 'date', 'time'],
      requiredFields: ['name', 'phone', 'date', 'service'],
      decisionTitle: 'Ready to book?',
      yes: {
        kind: 'booking',
        title: 'Book appointment',
        description: 'Create the appointment and check availability.',
        config: { service: 'Dental appointment', duration: 30, calendar: 'Dental calendar' },
        followUp: {
          kind: 'contact',
          title: 'Send confirmation',
          description: 'Send the booking confirmation by SMS.',
          config: { channel: 'sms', template: 'Your dental appointment is confirmed.' },
        },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer common questions',
        description: 'Use the FAQ to answer preparation or pricing questions.',
        config: { baseName: 'Dental FAQ', fallbackMessage: 'I can pass this to the team if needed.' },
        followUp: {
          kind: 'transfer',
          title: 'Route to staff',
          description: 'Transfer if the question needs a human.',
          config: { target: 'Front desk', announce: 'I will connect you with the front desk.' },
        },
      },
    }
  ),
  createMeta(
    'medical_clinic_intake',
    'healthcare',
    'Medical clinic intake',
    'Collect patient information, reason for visit, urgency level, and preferred appointment time.',
    'clipboard',
    ['intake', 'patient data', 'triage'],
    false,
    'intake',
    {
      messageText: 'I will collect your basic details and the reason for the visit.',
      collectFields: ['name', 'phone', 'service', 'urgency', 'preferred_time'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we need to escalate?',
      yes: {
        kind: 'booking',
        title: 'Book appointment',
        description: 'Book the patient into the next available slot.',
        config: { service: 'Clinic intake', duration: 20, calendar: 'Clinic calendar' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to admin',
        description: 'Route the patient to the right administrative process.',
        config: { target: 'Clinic admin', announce: 'I will connect you with our clinic team.' },
      },
    }
  ),
  createMeta(
    'after_hours_patient_call_handling',
    'healthcare',
    'After-hours patient call handling',
    'Answer after-hours calls, collect details, detect urgent cases, and route appropriately.',
    'moon',
    ['after-hours', 'urgent routing', 'patient support'],
    false,
    'handoff',
    {
      messageText: 'I am handling calls after hours and will collect the important details.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Emergency routing',
        description: 'Route urgent cases using the safe routing process.',
        config: { target: 'On-call team', announce: 'I will route this to the on-call team.' },
      },
      no: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Send a next-step message for the morning team.',
        config: { channel: 'sms', template: 'We received your message and will follow up during business hours.' },
      },
    }
  ),
  createMeta(
    'appointment_reminder_and_confirmation',
    'healthcare',
    'Appointment reminder and confirmation',
    'Confirm upcoming appointments and reduce no-shows with automated reminders.',
    'bell',
    ['reminders', 'confirmation', 'no-shows'],
    false,
    'followup',
    {
      messageText: 'I will confirm the upcoming appointment and reduce no-shows.',
      collectFields: ['name', 'phone', 'service', 'date', 'time'],
      requiredFields: ['name', 'phone', 'date'],
      decisionTitle: 'Should we send the reminder?',
      yes: {
        kind: 'contact',
        title: 'Send reminder',
        description: 'Send the reminder by text or email.',
        config: { channel: 'sms', template: 'Your appointment is coming up soon.' },
      },
      no: {
        kind: 'end',
        title: 'Skip reminder',
        description: 'Leave the appointment unchanged.',
        config: { closing: 'No reminder will be sent yet.' },
      },
    }
  ),
  createMeta(
    'new_patient_registration',
    'healthcare',
    'New patient registration',
    'Collect basic patient information, service needed, and preferred availability.',
    'clipboard',
    ['registration', 'patient intake', 'forms'],
    false,
    'intake',
    {
      messageText: 'I will register you and capture the information we need.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have the basics?',
      yes: {
        kind: 'booking',
        title: 'Book first visit',
        description: 'Schedule the first appointment.',
        config: { service: 'New patient visit', duration: 30, calendar: 'Clinic calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request missing details',
        description: 'Ask for the missing registration details.',
        config: { channel: 'sms', template: 'Please reply with the missing registration details.' },
      },
    }
  ),
  createMeta(
    'clinic_faq_assistant',
    'healthcare',
    'Clinic FAQ assistant',
    'Answer questions about hours, services, pricing, preparation, insurance, and location.',
    'knowledge',
    ['faq', 'hours', 'insurance'],
    false,
    'knowledge',
    {
      messageText: 'I can answer common clinic questions using the knowledge base.',
      collectFields: ['service', 'question'],
      requiredFields: ['question'],
      decisionTitle: 'Can the FAQ answer it?',
      yes: {
        kind: 'knowledge',
        title: 'Answer from knowledge base',
        description: 'Use the clinic FAQ to answer the question.',
        config: { baseName: 'Clinic FAQ', fallbackMessage: 'I can check this for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Transfer to clinic staff',
        description: 'Route the caller to the right person.',
        config: { target: 'Clinic staff', announce: 'I will transfer you to the clinic team.' },
      },
    }
  ),
  createMeta(
    'prescription_or_document_request_triage',
    'healthcare',
    'Prescription or document request triage',
    'Collect the request type and route it to the right administrative process.',
    'document',
    ['document requests', 'admin', 'triage'],
    false,
    'handoff',
    {
      messageText: 'I will note the request and route it to the right process.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we process this here?',
      yes: {
        kind: 'knowledge',
        title: 'Check documents',
        description: 'Use the knowledge base to verify the request.',
        config: { baseName: 'Document policy', fallbackMessage: 'I can check the policy for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to admin',
        description: 'Pass the request to the administrative team.',
        config: { target: 'Admin team', announce: 'I will route this to the admin team.' },
      },
    }
  ),
  createMeta(
    'emergency_escalation_workflow',
    'healthcare',
    'Emergency escalation workflow',
    'Detect urgent health-related language and provide safe routing or transfer instructions.',
    'alert',
    ['urgent', 'escalation', 'safety'],
    false,
    'handoff',
    {
      messageText: 'I will assess the situation and route it safely if needed.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Is this an urgent case?',
      yes: {
        kind: 'transfer',
        title: 'Emergency transfer',
        description: 'Route to the on-call or emergency team.',
        config: { target: 'On-call medical team', announce: 'I am transferring this to the urgent team now.' },
      },
      no: {
        kind: 'contact',
        title: 'Send safe follow-up',
        description: 'Send a safe next-step message.',
        config: { channel: 'sms', template: 'We received your message and will follow up as soon as possible.' },
      },
    }
  ),

  createMeta(
    'law_firm_intake',
    'professional_services',
    'Law firm intake',
    'Ask qualifying questions, identify case type, collect contact details, and route to the right lawyer.',
    'scales',
    ['intake', 'legal', 'qualification'],
    false,
    'intake',
    {
      messageText: 'I will ask a few intake questions to route the case properly.',
      collectFields: ['name', 'phone', 'service', 'case_type', 'notes'],
      requiredFields: ['name', 'phone', 'notes'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Transfer to lawyer',
        description: 'Route the caller to the intake team or the right lawyer.',
        config: { target: 'Intake team', announce: 'I will transfer you to the intake team.' },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer FAQ',
        description: 'Answer common legal questions from the knowledge base.',
        config: { baseName: 'Legal FAQ', fallbackMessage: 'I can review that with you.' },
      },
    }
  ),
  createMeta(
    'accounting_firm_client_intake',
    'professional_services',
    'Accounting firm client intake',
    'Collect business or tax information, determine service needs, and schedule a consultation.',
    'calculator',
    ['intake', 'tax', 'consultation'],
    false,
    'intake',
    {
      messageText: 'I will collect the details needed to route your accounting request.',
      collectFields: ['name', 'phone', 'service', 'business_name', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have enough to schedule?',
      yes: {
        kind: 'booking',
        title: 'Book consultation',
        description: 'Schedule a consultation with the accounting team.',
        config: { service: 'Accounting consultation', duration: 30, calendar: 'Accounting calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request details',
        description: 'Ask for any missing details by SMS or email.',
        config: { channel: 'email', subject: 'Accounting intake', template: 'Please reply with the missing business details.' },
      },
    }
  ),
  createMeta(
    'consulting_lead_qualification',
    'professional_services',
    'Consulting lead qualification',
    'Understand the business problem, budget, timeline, and decision-maker before booking.',
    'briefcase',
    ['lead qualification', 'consulting', 'sales'],
    false,
    'intake',
    {
      messageText: 'I will learn a little about your business and what you want to achieve.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is the lead qualified?',
      yes: {
        kind: 'booking',
        title: 'Book consultation',
        description: 'Set up a consulting call.',
        config: { service: 'Consulting call', duration: 45, calendar: 'Consulting calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send qualification follow-up',
        description: 'Send the next step by email.',
        config: { channel: 'email', subject: 'Consulting follow-up', template: 'Thanks for reaching out. We will review your request.' },
      },
    }
  ),
  createMeta(
    'insurance_quote_intake',
    'professional_services',
    'Insurance quote intake',
    'Collect client details, coverage needs, and route to an advisor.',
    'shield',
    ['insurance', 'quotes', 'routing'],
    false,
    'intake',
    {
      messageText: 'I will collect the details needed for an insurance quote.',
      collectFields: ['name', 'phone', 'service', 'coverage_type', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we quote this now?',
      yes: {
        kind: 'transfer',
        title: 'Route to advisor',
        description: 'Send the quote request to the right advisor.',
        config: { target: 'Insurance advisor', announce: 'I will route you to an advisor.' },
      },
      no: {
        kind: 'contact',
        title: 'Request more information',
        description: 'Send a message asking for the missing information.',
        config: { channel: 'sms', template: 'Please reply with the missing details so we can prepare your quote.' },
      },
    }
  ),
  createMeta(
    'real_estate_legal_intake',
    'professional_services',
    'Real estate legal intake',
    'Collect transaction details, deadlines, and documents needed for a legal consultation.',
    'home',
    ['real estate', 'legal', 'intake'],
    false,
    'intake',
    {
      messageText: 'I will collect the transaction details and important deadlines.',
      collectFields: ['name', 'phone', 'service', 'deadline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this ready for legal review?',
      yes: {
        kind: 'booking',
        title: 'Book legal consult',
        description: 'Schedule a consultation with the legal team.',
        config: { service: 'Real estate legal consult', duration: 30, calendar: 'Legal calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request documents',
        description: 'Ask the client to send the missing documents.',
        config: { channel: 'email', subject: 'Document request', template: 'Please send the documents we discussed.' },
      },
    }
  ),
  createMeta(
    'professional_callback_request',
    'professional_services',
    'Professional callback request',
    'Capture the reason for the call, urgency, contact details, and preferred callback time.',
    'phone-return',
    ['callback', 'reception', 'routing'],
    false,
    'callback',
    {
      messageText: 'I will capture your callback request and route it properly.',
      collectFields: ['name', 'phone', 'service', 'preferred_time', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Do we have enough for a callback?',
      yes: {
        kind: 'contact',
        title: 'Send callback confirmation',
        description: 'Confirm the callback by message.',
        config: { channel: 'sms', template: 'Thanks, we will call you back shortly.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to receptionist',
        description: 'Connect to the reception or intake team.',
        config: { target: 'Reception', announce: 'I will connect you with reception.' },
      },
    }
  ),
  createMeta(
    'document_collection_workflow',
    'professional_services',
    'Document collection workflow',
    'Ask for required documents and send instructions or upload links.',
    'file',
    ['documents', 'uploads', 'admin'],
    false,
    'followup',
    {
      messageText: 'I will let you know which documents are needed.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Can we request the documents now?',
      yes: {
        kind: 'contact',
        title: 'Send upload instructions',
        description: 'Send the document upload or email instructions.',
        config: { channel: 'email', subject: 'Documents needed', template: 'Please upload the requested documents using the link we sent.' },
      },
      no: {
        kind: 'end',
        title: 'Pause the request',
        description: 'Hold the workflow until the team is ready.',
        config: { closing: 'We will follow up when the team is ready.' },
      },
    }
  ),
  createMeta(
    'paid_consultation_booking',
    'professional_services',
    'Paid consultation booking',
    'Qualify the request, book a consultation, and request payment or deposit.',
    'payment',
    ['booking', 'payment', 'consultation'],
    false,
    'payment',
    {
      messageText: 'I can book a consultation and request payment if needed.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Ready to book and pay?',
      yes: {
        kind: 'payment',
        title: 'Request deposit',
        description: 'Send the payment link for the consultation.',
        config: { mode: 'deposit', amount: 100, percentage: 25, currency: 'USD', message: 'I can send the consultation payment link.' },
      },
      no: {
        kind: 'booking',
        title: 'Book consultation',
        description: 'Book the consultation and handle payment later.',
        config: { service: 'Paid consultation', duration: 45, calendar: 'Consulting calendar' },
      },
    }
  ),

  createMeta(
    'plumber_emergency_call',
    'home_services',
    'Plumber emergency call',
    'Capture the issue, urgency, address, availability, and send follow-up details.',
    'wrench',
    ['emergency', 'routing', 'dispatch'],
    false,
    'handoff',
    {
      messageText: 'Tell me what is happening and I will help route it correctly.',
      collectFields: ['name', 'phone', 'service', 'address', 'notes'],
      requiredFields: ['name', 'phone', 'address'],
      decisionTitle: 'Is it an emergency?',
      yes: {
        kind: 'transfer',
        title: 'Dispatch emergency tech',
        description: 'Route to the emergency technician or on-call person.',
        config: { target: 'Emergency tech', announce: 'I am dispatching an emergency technician.' },
      },
      no: {
        kind: 'booking',
        title: 'Schedule visit',
        description: 'Book a standard plumbing visit.',
        config: { service: 'Plumbing visit', duration: 60, calendar: 'Dispatch calendar' },
      },
    }
  ),
  createMeta(
    'hvac_service_booking',
    'home_services',
    'HVAC service booking',
    'Identify the problem, collect equipment details, book a technician, and confirm.',
    'fan',
    ['service booking', 'dispatch', 'hvac'],
    false,
    'booking',
    {
      messageText: 'I will capture the problem and help schedule a technician.',
      collectFields: ['name', 'phone', 'service', 'equipment', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we book the visit?',
      yes: {
        kind: 'booking',
        title: 'Book HVAC visit',
        description: 'Create the HVAC service booking.',
        config: { service: 'HVAC service', duration: 60, calendar: 'HVAC calendar' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to advisor',
        description: 'Send to the HVAC advisor if more detail is required.',
        config: { target: 'HVAC advisor', announce: 'I will connect you with an HVAC advisor.' },
      },
    }
  ),
  createMeta(
    'electrician_service_request',
    'home_services',
    'Electrician service request',
    'Collect the issue type, safety urgency, address, and preferred appointment time.',
    'bolt',
    ['service request', 'safety', 'booking'],
    false,
    'intake',
    {
      messageText: 'I will capture the issue and help route it safely.',
      collectFields: ['name', 'phone', 'service', 'address', 'notes'],
      requiredFields: ['name', 'phone', 'address'],
      decisionTitle: 'Is there a safety concern?',
      yes: {
        kind: 'transfer',
        title: 'Safety escalation',
        description: 'Route urgent electrical issues immediately.',
        config: { target: 'On-call electrician', announce: 'I will connect you with an electrician right away.' },
      },
      no: {
        kind: 'booking',
        title: 'Book electrician',
        description: 'Schedule a standard service visit.',
        config: { service: 'Electrician service', duration: 60, calendar: 'Electrical calendar' },
      },
    }
  ),
  createMeta(
    'cleaning_service_quote',
    'home_services',
    'Cleaning service quote',
    'Ask about property size, service type, frequency, and provide next steps.',
    'sparkles',
    ['quote', 'property', 'estimation'],
    false,
    'intake',
    {
      messageText: 'I will ask a few quick questions and prepare the next step.',
      collectFields: ['name', 'phone', 'service', 'property_size', 'frequency', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we quote it now?',
      yes: {
        kind: 'contact',
        title: 'Send quote follow-up',
        description: 'Send a follow-up message or quote request.',
        config: { channel: 'email', subject: 'Cleaning quote', template: 'Thanks. We will prepare your quote and follow up.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to estimator',
        description: 'Send the request to the estimator.',
        config: { target: 'Estimator', announce: 'I will route this to the estimator.' },
      },
    }
  ),
  createMeta(
    'roofing_estimate_request',
    'home_services',
    'Roofing estimate request',
    'Collect property details, issue description, photos if needed, and book an inspection.',
    'roof',
    ['estimate', 'inspection', 'property'],
    false,
    'intake',
    {
      messageText: 'I will gather the details needed for a roofing estimate.',
      collectFields: ['name', 'phone', 'service', 'address', 'notes'],
      requiredFields: ['name', 'phone', 'address'],
      decisionTitle: 'Do we need an inspection?',
      yes: {
        kind: 'booking',
        title: 'Book inspection',
        description: 'Schedule a roofing inspection.',
        config: { service: 'Roofing inspection', duration: 45, calendar: 'Roofing calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request photos',
        description: 'Ask for photos or extra details by message.',
        config: { channel: 'sms', template: 'Please send a few photos so we can prepare your estimate.' },
      },
    }
  ),
  createMeta(
    'pest_control_intake',
    'home_services',
    'Pest control intake',
    'Identify pest type, severity, location, and schedule a technician.',
    'bug',
    ['intake', 'pest control', 'booking'],
    false,
    'intake',
    {
      messageText: 'I will capture the problem and schedule the right technician.',
      collectFields: ['name', 'phone', 'service', 'address', 'notes'],
      requiredFields: ['name', 'phone', 'address', 'service'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Urgent routing',
        description: 'Route urgent infestations to the on-call team.',
        config: { target: 'Pest control team', announce: 'I will connect you with the pest team now.' },
      },
      no: {
        kind: 'booking',
        title: 'Book technician',
        description: 'Schedule a pest control visit.',
        config: { service: 'Pest control', duration: 45, calendar: 'Pest calendar' },
      },
    }
  ),
  createMeta(
    'home_renovation_quote',
    'home_services',
    'Home renovation quote',
    'Collect project type, budget, timeline, and route to the right estimator.',
    'hammer',
    ['quote', 'renovation', 'estimate'],
    false,
    'intake',
    {
      messageText: 'I will collect a few details so we can route the estimate properly.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we quote it now?',
      yes: {
        kind: 'transfer',
        title: 'Route to estimator',
        description: 'Send the project to the right estimator.',
        config: { target: 'Estimator', announce: 'I will route you to the renovation estimator.' },
      },
      no: {
        kind: 'contact',
        title: 'Request more project details',
        description: 'Send a follow-up message for missing details.',
        config: { channel: 'email', subject: 'Renovation quote', template: 'Please reply with a few more project details.' },
      },
    }
  ),
  createMeta(
    'missed_call_contractor_recovery',
    'home_services',
    'Missed-call contractor recovery',
    'Automatically follow up with missed callers and capture job opportunities.',
    'phone-return',
    ['missed calls', 'contractor', 'recovery'],
    false,
    'callback',
    {
      messageText: 'I am calling back to capture the job opportunity.',
      collectFields: ['name', 'phone', 'service', 'address', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we book a visit?',
      yes: {
        kind: 'booking',
        title: 'Book contractor visit',
        description: 'Schedule the contractor visit.',
        config: { service: 'Contractor visit', duration: 60, calendar: 'Contractor calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send missed-call follow-up',
        description: 'Send a missed-call follow-up SMS.',
        config: { channel: 'sms', template: 'Sorry we missed you. We will follow up shortly.' },
      },
    }
  ),

  createMeta(
    'buyer_qualification',
    'real_estate',
    'Buyer qualification',
    'Qualify buyers by budget, location, timeline, financing status, and property type.',
    'house',
    ['buyer', 'qualification', 'sales'],
    false,
    'intake',
    {
      messageText: 'I will ask a few questions so the right agent can help you.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is the buyer qualified?',
      yes: {
        kind: 'transfer',
        title: 'Route to agent',
        description: 'Connect the buyer with the right agent.',
        config: { target: 'Buyer agent', announce: 'I will route you to a buyer agent.' },
      },
      no: {
        kind: 'contact',
        title: 'Send next steps',
        description: 'Send a follow-up with the next step.',
        config: { channel: 'email', subject: 'Buyer follow-up', template: 'Thanks for your interest. We will follow up shortly.' },
      },
    }
  ),
  createMeta(
    'seller_lead_qualification',
    'real_estate',
    'Seller lead qualification',
    'Collect property details, motivation, timeline, and book a valuation call.',
    'home',
    ['seller', 'valuation', 'booking'],
    false,
    'intake',
    {
      messageText: 'I will collect the details needed for a valuation call.',
      collectFields: ['name', 'phone', 'service', 'property_address', 'timeline'],
      requiredFields: ['name', 'phone', 'service', 'property_address'],
      decisionTitle: 'Ready to book valuation?',
      yes: {
        kind: 'booking',
        title: 'Book valuation',
        description: 'Schedule the valuation call.',
        config: { service: 'Property valuation', duration: 30, calendar: 'Valuation calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Ask for the missing seller details.',
        config: { channel: 'sms', template: 'Please reply with the missing property details.' },
      },
    }
  ),
  createMeta(
    'property_showing_booking',
    'real_estate',
    'Property showing booking',
    'Capture buyer details, preferred time, property interest, and confirm a showing.',
    'calendar',
    ['showing', 'booking', 'viewing'],
    false,
    'booking',
    {
      messageText: 'I will help schedule the showing and capture the key details.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'notes'],
      requiredFields: ['name', 'phone', 'service', 'date', 'time'],
      decisionTitle: 'Is the buyer ready to book?',
      yes: {
        kind: 'booking',
        title: 'Book showing',
        description: 'Create the property showing booking.',
        config: { service: 'Property showing', duration: 30, calendar: 'Showings calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send showing options',
        description: 'Send the available showing options.',
        config: { channel: 'sms', template: 'Please reply with the time that works best for the showing.' },
      },
    }
  ),
  createMeta(
    'rental_inquiry_workflow',
    'real_estate',
    'Rental inquiry workflow',
    'Ask about move-in date, budget, household details, and schedule a viewing.',
    'key',
    ['rental', 'inquiry', 'viewing'],
    false,
    'intake',
    {
      messageText: 'I will capture the details needed for a rental viewing.',
      collectFields: ['name', 'phone', 'service', 'budget', 'move_in_date', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have the basics?',
      yes: {
        kind: 'booking',
        title: 'Book viewing',
        description: 'Schedule the rental viewing.',
        config: { service: 'Rental viewing', duration: 20, calendar: 'Rental calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request missing details',
        description: 'Ask for the missing rental information.',
        config: { channel: 'email', subject: 'Rental inquiry', template: 'Please reply with the missing rental details.' },
      },
    }
  ),
  createMeta(
    'mortgage_prequalification_routing',
    'real_estate',
    'Mortgage prequalification routing',
    'Collect basic financing information and route to a mortgage partner or agent.',
    'bank',
    ['mortgage', 'financing', 'routing'],
    false,
    'handoff',
    {
      messageText: 'I will gather the basics and route you to the right mortgage partner.',
      collectFields: ['name', 'phone', 'service', 'budget', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we route this now?',
      yes: {
        kind: 'transfer',
        title: 'Route to mortgage partner',
        description: 'Send the lead to the mortgage partner.',
        config: { target: 'Mortgage partner', announce: 'I will connect you with a mortgage partner.' },
      },
      no: {
        kind: 'contact',
        title: 'Send prequalification next steps',
        description: 'Send the missing details by email.',
        config: { channel: 'email', subject: 'Mortgage next steps', template: 'Please reply with the missing financing details.' },
      },
    }
  ),
  createMeta(
    'open_house_follow_up',
    'real_estate',
    'Open house follow-up',
    'Capture visitor details, interest level, and trigger personalized follow-up.',
    'followup',
    ['follow-up', 'open house', 'lead'],
    false,
    'followup',
    {
      messageText: 'I will capture your interest and send the right follow-up.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Should we send follow-up now?',
      yes: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Send the open house follow-up by message.',
        config: { channel: 'email', subject: 'Open house follow-up', template: 'Thanks for visiting. We will send more details shortly.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to agent',
        description: 'Pass the lead to the right real estate agent.',
        config: { target: 'Real estate agent', announce: 'I will connect you with the agent.' },
      },
    }
  ),
  createMeta(
    'real_estate_callback_assistant',
    'real_estate',
    'Real estate callback assistant',
    'Collect the reason for inquiry and route to the right agent.',
    'phone-return',
    ['callback', 'lead routing', 'agent'],
    false,
    'callback',
    {
      messageText: 'I will capture the reason for your call and route it properly.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have enough to route it?',
      yes: {
        kind: 'transfer',
        title: 'Route to agent',
        description: 'Transfer to the right agent.',
        config: { target: 'Real estate agent', announce: 'I will connect you with the right agent.' },
      },
      no: {
        kind: 'contact',
        title: 'Request more info',
        description: 'Send a callback confirmation or follow-up.',
        config: { channel: 'sms', template: 'Please reply with the missing details so we can help.' },
      },
    }
  ),
  createMeta(
    'property_management_request',
    'real_estate',
    'Property management request',
    'Collect tenant or owner requests, classify urgency, and create a task.',
    'building',
    ['property management', 'task', 'urgency'],
    false,
    'handoff',
    {
      messageText: 'I will collect the request and classify it for the property team.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Urgent escalation',
        description: 'Route the request to the property manager.',
        config: { target: 'Property manager', announce: 'I will escalate this to the property manager now.' },
      },
      no: {
        kind: 'contact',
        title: 'Create task follow-up',
        description: 'Send a follow-up and create a task for the team.',
        config: { channel: 'email', subject: 'Property management request', template: 'We received your request and created a task for the team.' },
      },
    }
  ),

  createMeta(
    'business_receptionist',
    'general_business',
    'Business receptionist',
    'Answer calls, understand the request, route, book, or collect contact details.',
    'reception',
    ['reception', 'routing', 'calls'],
    false,
    'callback',
    {
      messageText: 'I will answer the call and route it to the right place.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'How should we help?',
      yes: {
        kind: 'booking',
        title: 'Book meeting',
        description: 'Book a call or meeting if needed.',
        config: { service: 'Reception booking', duration: 30, calendar: 'Reception calendar' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to team',
        description: 'Transfer to the right person or team.',
        config: { target: 'Team member', announce: 'I will connect you with the right person.' },
      },
    }
  ),
  createMeta(
    'sales_lead_intake',
    'general_business',
    'Sales lead intake',
    'Qualify leads, collect needs, budget, timeline, and send to sales.',
    'lead',
    ['sales', 'lead qualification', 'revenue'],
    false,
    'intake',
    {
      messageText: 'I will ask a few sales questions so we can route the lead properly.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is the lead ready for sales?',
      yes: {
        kind: 'transfer',
        title: 'Notify sales',
        description: 'Pass the lead to sales.',
        config: { target: 'Sales team', announce: 'I will connect you with sales.' },
      },
      no: {
        kind: 'contact',
        title: 'Send nurture follow-up',
        description: 'Send a follow-up message for later sales engagement.',
        config: { channel: 'email', subject: 'Thanks for reaching out', template: 'We will follow up with the next best step.' },
      },
    }
  ),
  createMeta(
    'customer_support_triage',
    'general_business',
    'Customer support triage',
    'Classify requests, answer common questions, escalate, or create a support ticket.',
    'support',
    ['support', 'ticket', 'triage'],
    false,
    'support',
    {
      messageText: 'I will try to answer your question or route it to the right team.',
      collectFields: ['name', 'phone', 'service', 'notes', 'order_id'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Can we solve this now?',
      yes: {
        kind: 'knowledge',
        title: 'Answer FAQ',
        description: 'Use the support knowledge base to answer common questions.',
        config: { baseName: 'Support knowledge base', fallbackMessage: 'I can check that for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Create support ticket',
        description: 'Escalate the request to support or create a ticket.',
        config: { target: 'Support team', announce: 'I will transfer you to support.' },
      },
    }
  ),
  createMeta(
    'quote_request_workflow',
    'general_business',
    'Quote request workflow',
    'Collect project details, budget, timeline, and route to the right team.',
    'document',
    ['quote', 'estimate', 'routing'],
    false,
    'intake',
    {
      messageText: 'I will collect the details needed for a quote request.',
      collectFields: ['name', 'phone', 'service', 'budget', 'timeline', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we quote it now?',
      yes: {
        kind: 'transfer',
        title: 'Route to estimator',
        description: 'Send the quote request to the right team.',
        config: { target: 'Estimator', announce: 'I will route this to the estimator.' },
      },
      no: {
        kind: 'contact',
        title: 'Send request for details',
        description: 'Ask for the missing project details.',
        config: { channel: 'email', subject: 'Quote request', template: 'Please reply with a few more project details.' },
      },
    }
  ),
  createMeta(
    'appointment_and_consultation_booking',
    'general_business',
    'Appointment and consultation booking',
    'Book meetings, send confirmations, and collect required information.',
    'calendar',
    ['booking', 'consultation', 'confirmation'],
    false,
    'booking',
    {
      messageText: 'I can help book the meeting or consultation you need.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'notes'],
      requiredFields: ['name', 'phone', 'service', 'date'],
      decisionTitle: 'Are we ready to book?',
      yes: {
        kind: 'booking',
        title: 'Book consultation',
        description: 'Create the appointment.',
        config: { service: 'Consultation', duration: 30, calendar: 'Main calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send confirmation details',
        description: 'Send the booking requirements by message.',
        config: { channel: 'sms', template: 'Please reply with the missing details so we can schedule your consultation.' },
      },
    }
  ),
  createMeta(
    'payment_collection_workflow',
    'general_business',
    'Payment collection workflow',
    'Request a deposit or payment link after confirming the service.',
    'payment',
    ['payments', 'deposit', 'collection'],
    false,
    'payment',
    {
      messageText: 'I will confirm the service and send the payment step if needed.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should we request payment now?',
      yes: {
        kind: 'payment',
        title: 'Request payment',
        description: 'Send the payment link.',
        config: { mode: 'deposit', amount: 50, percentage: 20, currency: 'USD', message: 'I can send the payment link now.' },
      },
      no: {
        kind: 'booking',
        title: 'Continue service flow',
        description: 'Continue without payment if not needed yet.',
        config: { service: 'Service request', duration: 30, calendar: 'Main calendar' },
      },
    }
  ),
  createMeta(
    'internal_task_creation',
    'general_business',
    'Internal task creation',
    'Convert customer requests into internal tasks or notifications.',
    'task',
    ['internal task', 'operations', 'notifications'],
    false,
    'handoff',
    {
      messageText: 'I will capture the request and create the right internal task.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should we create a task now?',
      yes: {
        kind: 'webhook',
        title: 'Create internal task',
        description: 'Trigger an internal task or notification.',
        config: { method: 'POST', url: 'https://example.com/internal-task', secret: '' },
      },
      no: {
        kind: 'contact',
        title: 'Send recap',
        description: 'Send a quick recap message.',
        config: { channel: 'email', subject: 'Request recap', template: 'We received your request and will create a task.' },
      },
    }
  ),
  createMeta(
    'vip_or_urgent_request_routing',
    'general_business',
    'VIP or urgent request routing',
    'Detect priority customers or urgent requests and notify the right person immediately.',
    'priority',
    ['urgent', 'vip', 'escalation'],
    false,
    'handoff',
    {
      messageText: 'I will detect priority or urgent requests and route them immediately.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Is this urgent or VIP?',
      yes: {
        kind: 'transfer',
        title: 'Priority handoff',
        description: 'Notify the right person immediately.',
        config: { target: 'Priority contact', announce: 'I will connect you with the priority contact now.' },
      },
      no: {
        kind: 'contact',
        title: 'Normal follow-up',
        description: 'Send the request to the normal follow-up path.',
        config: { channel: 'sms', template: 'Thanks. We will follow up shortly.' },
      },
    }
  ),

  createMeta(
    'salon_appointment_booking',
    'beauty_wellness',
    'Salon appointment booking',
    'Collect service type, preferred stylist, date, time, and confirm the booking.',
    'scissors',
    ['booking', 'beauty', 'confirmation'],
    false,
    'booking',
    {
      messageText: 'I will help you book the salon appointment you want.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'preferred_stylist'],
      requiredFields: ['name', 'phone', 'service', 'date', 'time'],
      decisionTitle: 'Ready to book the salon visit?',
      yes: {
        kind: 'booking',
        title: 'Book salon visit',
        description: 'Create the salon booking.',
        config: { service: 'Salon appointment', duration: 45, calendar: 'Salon calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send stylist options',
        description: 'Send the next available options by message.',
        config: { channel: 'sms', template: 'Please reply with the time and stylist preference that works best.' },
      },
    }
  ),
  createMeta(
    'spa_treatment_booking',
    'beauty_wellness',
    'Spa treatment booking',
    'Recommend or book treatments based on client needs and availability.',
    'spa',
    ['booking', 'spa', 'recommendation'],
    false,
    'booking',
    {
      messageText: 'I will help recommend or book the spa treatment that fits your needs.',
      collectFields: ['name', 'phone', 'service', 'preferred_time', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should we book a treatment?',
      yes: {
        kind: 'booking',
        title: 'Book spa treatment',
        description: 'Book the treatment and check availability.',
        config: { service: 'Spa treatment', duration: 60, calendar: 'Spa calendar' },
      },
      no: {
        kind: 'knowledge',
        title: 'Suggest options',
        description: 'Use the knowledge base to suggest the right treatment.',
        config: { baseName: 'Spa treatments', fallbackMessage: 'I can suggest a few options.' },
      },
    }
  ),
  createMeta(
    'wellness_consultation_intake',
    'beauty_wellness',
    'Wellness consultation intake',
    'Collect goals, preferences, and availability before booking a consultation.',
    'leaf',
    ['intake', 'consultation', 'wellness'],
    false,
    'intake',
    {
      messageText: 'I will collect your goals and book the consultation if we have enough detail.',
      collectFields: ['name', 'phone', 'service', 'goals', 'preferred_time'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Ready to schedule the consultation?',
      yes: {
        kind: 'booking',
        title: 'Book consultation',
        description: 'Schedule the wellness consultation.',
        config: { service: 'Wellness consultation', duration: 30, calendar: 'Wellness calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request more detail',
        description: 'Send a follow-up asking for the missing details.',
        config: { channel: 'sms', template: 'Please reply with your goals and preferred time.' },
      },
    }
  ),
  createMeta(
    'no_show_prevention_reminders',
    'beauty_wellness',
    'No-show prevention reminders',
    'Send reminders and confirmation requests before appointments.',
    'bell',
    ['reminders', 'confirmation', 'rebooking'],
    false,
    'followup',
    {
      messageText: 'I will send appointment reminders to reduce no-shows.',
      collectFields: ['name', 'phone', 'service', 'date', 'time'],
      requiredFields: ['name', 'phone', 'date'],
      decisionTitle: 'Should we send reminders now?',
      yes: {
        kind: 'contact',
        title: 'Send reminder',
        description: 'Send the reminder by SMS or email.',
        config: { channel: 'sms', template: 'Your appointment is coming up soon.' },
      },
      no: {
        kind: 'end',
        title: 'Skip reminder',
        description: 'No reminder will be sent yet.',
        config: { closing: 'No reminder will be sent yet.' },
      },
    }
  ),
  createMeta(
    'membership_or_package_inquiry',
    'beauty_wellness',
    'Membership or package inquiry',
    'Answer questions about packages, pricing, and book a follow-up.',
    'badge',
    ['membership', 'pricing', 'follow-up'],
    false,
    'knowledge',
    {
      messageText: 'I can answer questions about packages and set up a follow-up if needed.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Need a follow-up call?',
      yes: {
        kind: 'booking',
        title: 'Book follow-up',
        description: 'Book a call to discuss membership options.',
        config: { service: 'Membership consult', duration: 20, calendar: 'Membership calendar' },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer package questions',
        description: 'Use the knowledge base to answer pricing or package questions.',
        config: { baseName: 'Package FAQ', fallbackMessage: 'I can check the current package details.' },
      },
    }
  ),
  createMeta(
    'new_client_intake',
    'beauty_wellness',
    'New client intake',
    'Collect contact details, preferences, service history, and notes.',
    'clipboard',
    ['intake', 'client profile', 'forms'],
    false,
    'intake',
    {
      messageText: 'I will collect the information we need for your new client profile.',
      collectFields: ['name', 'phone', 'service', 'preferences', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Do we have enough to continue?',
      yes: {
        kind: 'booking',
        title: 'Book first appointment',
        description: 'Schedule the first appointment.',
        config: { service: 'New client appointment', duration: 45, calendar: 'Client calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Request missing profile details',
        description: 'Ask for the missing details by text.',
        config: { channel: 'sms', template: 'Please reply with the missing profile details.' },
      },
    }
  ),
  createMeta(
    'deposit_request_for_appointment',
    'beauty_wellness',
    'Deposit request for appointment',
    'Request a deposit for high-value or long-duration appointments.',
    'payment',
    ['deposit', 'payments', 'booking'],
    false,
    'payment',
    {
      messageText: 'I can request a deposit once the appointment is confirmed.',
      collectFields: ['name', 'phone', 'service', 'date', 'time'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should we request the deposit now?',
      yes: {
        kind: 'payment',
        title: 'Request deposit',
        description: 'Send the deposit link.',
        config: { mode: 'deposit', amount: 50, percentage: 20, currency: 'USD', message: 'I can send the deposit link now.' },
      },
      no: {
        kind: 'booking',
        title: 'Continue booking',
        description: 'Continue with the booking without requesting the deposit yet.',
        config: { service: 'Beauty appointment', duration: 45, calendar: 'Beauty calendar' },
      },
    }
  ),
  createMeta(
    'post_visit_follow_up',
    'beauty_wellness',
    'Post-visit follow-up',
    'Ask for feedback, suggest next appointment, and encourage rebooking.',
    'followup',
    ['follow-up', 'feedback', 'rebooking'],
    false,
    'followup',
    {
      messageText: 'I will send a quick follow-up after the visit.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Send the follow-up now?',
      yes: {
        kind: 'contact',
        title: 'Send post-visit follow-up',
        description: 'Send the feedback or rebooking message.',
        config: { channel: 'sms', template: 'Thanks for visiting. We would love your feedback.' },
      },
      no: {
        kind: 'end',
        title: 'Close the workflow',
        description: 'End the follow-up flow for now.',
        config: { closing: 'Thanks, the follow-up has been skipped for now.' },
      },
    }
  ),

  createMeta(
    'auto_repair_appointment_booking',
    'automotive',
    'Auto repair appointment booking',
    'Collect vehicle details, issue description, availability, and book service.',
    'car',
    ['booking', 'vehicle', 'service'],
    false,
    'booking',
    {
      messageText: 'I will capture the vehicle issue and help book the right appointment.',
      collectFields: ['name', 'phone', 'service', 'vehicle', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we book the repair?',
      yes: {
        kind: 'booking',
        title: 'Book repair appointment',
        description: 'Schedule the repair slot.',
        config: { service: 'Auto repair', duration: 45, calendar: 'Service calendar' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to advisor',
        description: 'Transfer to a service advisor when more detail is needed.',
        config: { target: 'Service advisor', announce: 'I will connect you with a service advisor.' },
      },
    }
  ),
  createMeta(
    'tire_change_booking',
    'automotive',
    'Tire change booking',
    'Schedule tire installation, rotation, storage, or seasonal change.',
    'tire',
    ['booking', 'seasonal', 'service'],
    false,
    'booking',
    {
      messageText: 'I will help schedule your tire service.',
      collectFields: ['name', 'phone', 'service', 'vehicle', 'date', 'time'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Ready to book the tire service?',
      yes: {
        kind: 'booking',
        title: 'Book tire service',
        description: 'Create the tire service booking.',
        config: { service: 'Tire service', duration: 30, calendar: 'Service calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send service options',
        description: 'Send the available tire service options by message.',
        config: { channel: 'sms', template: 'Please reply with the time that works best for your tire service.' },
      },
    }
  ),
  createMeta(
    'vehicle_diagnostic_intake',
    'automotive',
    'Vehicle diagnostic intake',
    'Ask symptoms, urgency, vehicle model, and preferred appointment time.',
    'wrench',
    ['diagnostic', 'intake', 'vehicle'],
    false,
    'intake',
    {
      messageText: 'I will collect the symptoms and route the diagnostic properly.',
      collectFields: ['name', 'phone', 'service', 'vehicle', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Urgent service routing',
        description: 'Route urgent diagnostics to the service advisor.',
        config: { target: 'Service advisor', announce: 'I will connect you with a service advisor right away.' },
      },
      no: {
        kind: 'booking',
        title: 'Book diagnostic',
        description: 'Book the diagnostic appointment.',
        config: { service: 'Vehicle diagnostic', duration: 45, calendar: 'Service calendar' },
      },
    }
  ),
  createMeta(
    'dealership_lead_qualification',
    'automotive',
    'Dealership lead qualification',
    'Qualify buyers by vehicle interest, budget, trade-in, and financing needs.',
    'badge',
    ['lead qualification', 'sales', 'financing'],
    false,
    'intake',
    {
      messageText: 'I will qualify your vehicle interest and route it to the right sales person.',
      collectFields: ['name', 'phone', 'service', 'budget', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is the lead ready for sales?',
      yes: {
        kind: 'transfer',
        title: 'Route to sales',
        description: 'Pass the lead to the sales team.',
        config: { target: 'Sales team', announce: 'I will connect you with a sales person.' },
      },
      no: {
        kind: 'contact',
        title: 'Send vehicle follow-up',
        description: 'Send a follow-up with the next steps.',
        config: { channel: 'email', subject: 'Vehicle follow-up', template: 'Thanks for your interest. We will follow up shortly.' },
      },
    }
  ),
  createMeta(
    'test_drive_booking',
    'automotive',
    'Test drive booking',
    'Collect buyer details, vehicle interest, and schedule a test drive.',
    'calendar',
    ['booking', 'test drive', 'sales'],
    false,
    'booking',
    {
      messageText: 'I can help schedule your test drive.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'vehicle'],
      requiredFields: ['name', 'phone', 'service', 'date', 'time'],
      decisionTitle: 'Ready to book the test drive?',
      yes: {
        kind: 'booking',
        title: 'Book test drive',
        description: 'Create the test drive booking.',
        config: { service: 'Test drive', duration: 30, calendar: 'Sales calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send available times',
        description: 'Send the available times by message.',
        config: { channel: 'sms', template: 'Please reply with the time that works best for your test drive.' },
      },
    }
  ),
  createMeta(
    'service_reminder_workflow',
    'automotive',
    'Service reminder workflow',
    'Remind customers about maintenance, inspections, or follow-up service.',
    'bell',
    ['reminders', 'maintenance', 'follow-up'],
    false,
    'followup',
    {
      messageText: 'I will send a reminder about the upcoming service.',
      collectFields: ['name', 'phone', 'service', 'vehicle', 'date', 'time'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should we send the reminder?',
      yes: {
        kind: 'contact',
        title: 'Send reminder',
        description: 'Send the reminder by SMS or email.',
        config: { channel: 'sms', template: 'Your vehicle service is coming up soon.' },
      },
      no: {
        kind: 'end',
        title: 'Skip reminder',
        description: 'Leave the service reminder for later.',
        config: { closing: 'No reminder will be sent yet.' },
      },
    }
  ),
  createMeta(
    'tow_or_roadside_request',
    'automotive',
    'Tow or roadside request',
    'Capture location, vehicle issue, urgency, and route to assistance.',
    'tow',
    ['roadside', 'emergency', 'assistance'],
    false,
    'handoff',
    {
      messageText: 'I will gather the location and the vehicle issue so we can route help quickly.',
      collectFields: ['name', 'phone', 'service', 'location', 'notes'],
      requiredFields: ['name', 'phone', 'location'],
      decisionTitle: 'Is this urgent?',
      yes: {
        kind: 'transfer',
        title: 'Route to roadside assistance',
        description: 'Connect the caller to assistance quickly.',
        config: { target: 'Roadside assistance', announce: 'I will route this to roadside assistance now.' },
      },
      no: {
        kind: 'contact',
        title: 'Send assistance recap',
        description: 'Send the assistance recap by text.',
        config: { channel: 'sms', template: 'We received your roadside request and are reviewing it.' },
      },
    }
  ),
  createMeta(
    'parts_request_workflow',
    'automotive',
    'Parts request workflow',
    'Collect vehicle information and part needed, then notify the parts team.',
    'package',
    ['parts', 'inventory', 'routing'],
    false,
    'handoff',
    {
      messageText: 'I will capture the part request and route it to the parts team.',
      collectFields: ['name', 'phone', 'service', 'vehicle', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we route it now?',
      yes: {
        kind: 'webhook',
        title: 'Notify parts team',
        description: 'Trigger the parts request workflow or internal notification.',
        config: { method: 'POST', url: 'https://example.com/parts-request', secret: '' },
      },
      no: {
        kind: 'contact',
        title: 'Request more info',
        description: 'Ask for the missing part or vehicle details.',
        config: { channel: 'sms', template: 'Please reply with the part details and vehicle information.' },
      },
    }
  ),

  createMeta(
    'hotel_reservation_assistant',
    'hospitality',
    'Hotel reservation assistant',
    'Answer questions, collect dates, guest count, and route to booking.',
    'hotel',
    ['booking', 'hotel', 'guest support'],
    false,
    'booking',
    {
      messageText: 'I can help with your hotel reservation or questions.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'guest_count'],
      requiredFields: ['name', 'phone', 'service', 'date'],
      decisionTitle: 'Ready to reserve?',
      yes: {
        kind: 'booking',
        title: 'Book reservation',
        description: 'Create the hotel reservation.',
        config: { service: 'Hotel reservation', duration: 15, calendar: 'Reservations calendar' },
      },
      no: {
        kind: 'knowledge',
        title: 'Answer hotel questions',
        description: 'Answer common hotel questions from the knowledge base.',
        config: { baseName: 'Hotel FAQ', fallbackMessage: 'I can check that for you.' },
      },
    }
  ),
  createMeta(
    'restaurant_reservation_booking',
    'hospitality',
    'Restaurant reservation booking',
    'Collect date, time, party size, preferences, and confirm reservation.',
    'utensils',
    ['booking', 'reservation', 'dining'],
    false,
    'booking',
    {
      messageText: 'I will help book your table reservation.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'party_size'],
      requiredFields: ['name', 'phone', 'date', 'time', 'party_size'],
      decisionTitle: 'Do we have the reservation details?',
      yes: {
        kind: 'booking',
        title: 'Book table',
        description: 'Create the restaurant reservation.',
        config: { service: 'Table reservation', duration: 90, calendar: 'Restaurant calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send available times',
        description: 'Send the available reservation times.',
        config: { channel: 'sms', template: 'Please reply with the best time and party size for your table.' },
      },
    }
  ),
  createMeta(
    'guest_request_handling',
    'hospitality',
    'Guest request handling',
    'Handle requests for amenities, room service, maintenance, or front desk support.',
    'concierge',
    ['guest support', 'routing', 'room service'],
    false,
    'handoff',
    {
      messageText: 'I will capture the guest request and route it to the right place.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we handle this here?',
      yes: {
        kind: 'knowledge',
        title: 'Answer guest request',
        description: 'Use the knowledge base to answer common requests.',
        config: { baseName: 'Guest services', fallbackMessage: 'I can check that for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to front desk',
        description: 'Transfer to the front desk or correct department.',
        config: { target: 'Front desk', announce: 'I will connect you with the front desk.' },
      },
    }
  ),
  createMeta(
    'event_inquiry_workflow',
    'hospitality',
    'Event inquiry workflow',
    'Collect event type, guest count, date, budget, and route to event staff.',
    'calendar-event',
    ['events', 'inquiry', 'sales'],
    false,
    'intake',
    {
      messageText: 'I will gather the event details and route the inquiry properly.',
      collectFields: ['name', 'phone', 'service', 'guest_count', 'budget', 'date'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is this ready for events?',
      yes: {
        kind: 'transfer',
        title: 'Route to event staff',
        description: 'Send the inquiry to the event staff.',
        config: { target: 'Event staff', announce: 'I will connect you with events.' },
      },
      no: {
        kind: 'contact',
        title: 'Send event follow-up',
        description: 'Send a follow-up asking for the missing details.',
        config: { channel: 'email', subject: 'Event inquiry', template: 'Please reply with the missing event details.' },
      },
    }
  ),
  createMeta(
    'catering_quote_request',
    'hospitality',
    'Catering quote request',
    'Collect event details, menu preferences, budget, and follow-up information.',
    'tray',
    ['quote', 'catering', 'events'],
    false,
    'intake',
    {
      messageText: 'I will capture the catering request and prepare the next step.',
      collectFields: ['name', 'phone', 'service', 'budget', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Can we quote this now?',
      yes: {
        kind: 'transfer',
        title: 'Route to catering team',
        description: 'Send the quote request to catering.',
        config: { target: 'Catering team', announce: 'I will route this to catering.' },
      },
      no: {
        kind: 'contact',
        title: 'Request menu details',
        description: 'Ask for missing menu or event details.',
        config: { channel: 'email', subject: 'Catering quote', template: 'Please reply with the menu and event details.' },
      },
    }
  ),
  createMeta(
    'complaint_escalation',
    'hospitality',
    'Complaint escalation',
    'Detect dissatisfaction, collect details, and escalate to management.',
    'alert',
    ['complaint', 'escalation', 'management'],
    false,
    'handoff',
    {
      messageText: 'I will gather the complaint details and route it to management if needed.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should management step in?',
      yes: {
        kind: 'transfer',
        title: 'Escalate to management',
        description: 'Route the complaint to management.',
        config: { target: 'Management', announce: 'I will transfer you to management.' },
      },
      no: {
        kind: 'contact',
        title: 'Send apology follow-up',
        description: 'Send a follow-up and recap.',
        config: { channel: 'email', subject: 'We are reviewing your feedback', template: 'We received your feedback and are reviewing it.' },
      },
    }
  ),
  createMeta(
    'review_follow_up',
    'hospitality',
    'Review follow-up',
    'Ask for feedback after a visit and encourage positive reviews.',
    'review',
    ['reviews', 'follow-up', 'feedback'],
    false,
    'followup',
    {
      messageText: 'I will ask for feedback and encourage a review after the visit.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Send the review request now?',
      yes: {
        kind: 'contact',
        title: 'Send review request',
        description: 'Send the feedback or review request by message.',
        config: { channel: 'sms', template: 'Thanks for visiting. We would love your feedback.' },
      },
      no: {
        kind: 'end',
        title: 'Close the follow-up',
        description: 'End the follow-up for now.',
        config: { closing: 'No review request will be sent yet.' },
      },
    }
  ),
  createMeta(
    'group_booking_intake',
    'hospitality',
    'Group booking intake',
    'Collect group size, dates, special needs, and contact information.',
    'users',
    ['group booking', 'events', 'intake'],
    false,
    'booking',
    {
      messageText: 'I will capture the group booking details for the team.',
      collectFields: ['name', 'phone', 'service', 'date', 'time', 'guest_count', 'notes'],
      requiredFields: ['name', 'phone', 'service', 'date', 'guest_count'],
      decisionTitle: 'Ready to book the group?',
      yes: {
        kind: 'booking',
        title: 'Book group',
        description: 'Create the group booking or reservation.',
        config: { service: 'Group reservation', duration: 60, calendar: 'Group bookings calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send group details request',
        description: 'Ask for the missing group booking details.',
        config: { channel: 'email', subject: 'Group booking details', template: 'Please reply with the missing group booking details.' },
      },
    }
  ),

  createMeta(
    'product_availability_assistant',
    'retail',
    'Product availability assistant',
    'Answer questions about products, availability, pricing, and store details.',
    'shopping',
    ['availability', 'pricing', 'assistance'],
    false,
    'knowledge',
    {
      messageText: 'I can check product availability and answer store questions.',
      collectFields: ['name', 'phone', 'service', 'question'],
      requiredFields: ['question'],
      decisionTitle: 'Can we answer it now?',
      yes: {
        kind: 'knowledge',
        title: 'Answer product question',
        description: 'Use the knowledge base to answer product questions.',
        config: { baseName: 'Product FAQ', fallbackMessage: 'I can check that for you.' },
      },
      no: {
        kind: 'contact',
        title: 'Send product follow-up',
        description: 'Send a follow-up with the next steps.',
        config: { channel: 'sms', template: 'We are checking availability and will follow up shortly.' },
      },
    }
  ),
  createMeta(
    'order_status_assistant',
    'retail',
    'Order status assistant',
    'Help customers check order status, shipping updates, or pickup information.',
    'package',
    ['order status', 'shipping', 'support'],
    false,
    'support',
    {
      messageText: 'I will check the order status or route the request if needed.',
      collectFields: ['name', 'phone', 'service', 'order_id'],
      requiredFields: ['name', 'phone', 'order_id'],
      decisionTitle: 'Can we find the order?',
      yes: {
        kind: 'knowledge',
        title: 'Check order status',
        description: 'Use the order knowledge base or tracking data.',
        config: { baseName: 'Order tracking', fallbackMessage: 'I can check the order for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to support',
        description: 'Send the customer to the support team.',
        config: { target: 'Retail support', announce: 'I will connect you with support.' },
      },
    }
  ),
  createMeta(
    'return_and_exchange_workflow',
    'retail',
    'Return and exchange workflow',
    'Collect order details, reason for return, and explain next steps.',
    'return',
    ['returns', 'exchange', 'support'],
    false,
    'support',
    {
      messageText: 'I will collect the return details and explain the next steps.',
      collectFields: ['name', 'phone', 'service', 'order_id', 'notes'],
      requiredFields: ['name', 'phone', 'order_id'],
      decisionTitle: 'Can we process the return now?',
      yes: {
        kind: 'knowledge',
        title: 'Explain return steps',
        description: 'Answer the return or exchange process from the knowledge base.',
        config: { baseName: 'Returns policy', fallbackMessage: 'I can check the return steps for you.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to support',
        description: 'Transfer to the returns team.',
        config: { target: 'Returns team', announce: 'I will connect you with the returns team.' },
      },
    }
  ),
  createMeta(
    'e_commerce_lead_capture',
    'retail',
    'E-commerce lead capture',
    'Capture buyer interest and recommend next steps or products.',
    'cart',
    ['lead capture', 'e-commerce', 'sales'],
    false,
    'intake',
    {
      messageText: 'I will capture your interest and recommend the best next step.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Is the buyer ready to proceed?',
      yes: {
        kind: 'booking',
        title: 'Book product demo',
        description: 'Schedule a product demo or consultation.',
        config: { service: 'Product demo', duration: 20, calendar: 'Sales calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send product follow-up',
        description: 'Send a follow-up with product details.',
        config: { channel: 'email', subject: 'Product follow-up', template: 'Thanks for your interest. We will send more information shortly.' },
      },
    }
  ),
  createMeta(
    'abandoned_inquiry_follow_up',
    'retail',
    'Abandoned inquiry follow-up',
    'Follow up with customers who asked questions but did not complete purchase.',
    'followup',
    ['abandoned inquiry', 'follow-up', 'sales'],
    false,
    'followup',
    {
      messageText: 'I will follow up with the customer who did not complete the purchase.',
      collectFields: ['name', 'phone', 'service', 'notes'],
      requiredFields: ['name', 'phone'],
      decisionTitle: 'Should we send the follow-up now?',
      yes: {
        kind: 'contact',
        title: 'Send follow-up',
        description: 'Send the abandoned inquiry follow-up by email or text.',
        config: { channel: 'email', subject: 'We can help with your order', template: 'Thanks for your interest. We are here if you need help.' },
      },
      no: {
        kind: 'end',
        title: 'Close the flow',
        description: 'End the follow-up flow for now.',
        config: { closing: 'No follow-up will be sent yet.' },
      },
    }
  ),
  createMeta(
    'store_appointment_booking',
    'retail',
    'Store appointment booking',
    'Book in-store consultations, fittings, demos, or service appointments.',
    'calendar',
    ['booking', 'store', 'consultation'],
    false,
    'booking',
    {
      messageText: 'I can help book an in-store appointment for you.',
      collectFields: ['name', 'phone', 'service', 'date', 'time'],
      requiredFields: ['name', 'phone', 'service', 'date', 'time'],
      decisionTitle: 'Ready to book the appointment?',
      yes: {
        kind: 'booking',
        title: 'Book store appointment',
        description: 'Create the in-store booking.',
        config: { service: 'Store appointment', duration: 30, calendar: 'Store calendar' },
      },
      no: {
        kind: 'contact',
        title: 'Send available times',
        description: 'Send available booking times.',
        config: { channel: 'sms', template: 'Please reply with the time that works best for your appointment.' },
      },
    }
  ),
  createMeta(
    'customer_complaint_triage',
    'retail',
    'Customer complaint triage',
    'Collect issue details and route to support or management.',
    'support',
    ['complaint', 'triage', 'management'],
    false,
    'handoff',
    {
      messageText: 'I will capture the issue and route it to the right team.',
      collectFields: ['name', 'phone', 'service', 'order_id', 'notes'],
      requiredFields: ['name', 'phone', 'service'],
      decisionTitle: 'Should management see this?',
      yes: {
        kind: 'transfer',
        title: 'Escalate to management',
        description: 'Route the complaint to management.',
        config: { target: 'Management', announce: 'I will escalate this to management.' },
      },
      no: {
        kind: 'transfer',
        title: 'Route to support',
        description: 'Route to the support team.',
        config: { target: 'Support team', announce: 'I will connect you with support.' },
      },
    }
  ),
  createMeta(
    'loyalty_program_assistant',
    'retail',
    'Loyalty program assistant',
    'Answer questions about points, rewards, membership, and promotions.',
    'badge',
    ['loyalty', 'rewards', 'membership'],
    false,
    'knowledge',
    {
      messageText: 'I can help answer loyalty program questions.',
      collectFields: ['name', 'phone', 'service', 'question'],
      requiredFields: ['question'],
      decisionTitle: 'Can the loyalty FAQ answer this?',
      yes: {
        kind: 'knowledge',
        title: 'Answer loyalty questions',
        description: 'Use the loyalty knowledge base to answer the question.',
        config: { baseName: 'Loyalty FAQ', fallbackMessage: 'I can check your loyalty details.' },
      },
      no: {
        kind: 'contact',
        title: 'Send loyalty follow-up',
        description: 'Send a follow-up message with the next step.',
        config: { channel: 'email', subject: 'Loyalty follow-up', template: 'We will review your loyalty request and follow up.' },
      },
    }
  ),
]

function createTemplate(def) {
  return {
    ...def,
    build(agentId) {
      return buildTemplateWorkflow(def, agentId)
    },
  }
}

export const workflowTemplates = templateDefinitions.map(createTemplate)
export const WORKFLOW_TEMPLATES = workflowTemplates

export function buildWorkflowFromTemplate(templateKey, agentId) {
  const template = workflowTemplates.find((item) => item.id === templateKey || item.key === templateKey)
  if (!template) return null
  const built = template.build(agentId)
  return {
    id: null,
    agent_id: agentId || null,
    name: template.title,
    description: template.description,
    status: template.status,
    version: template.version,
    nodes: built.nodes,
    edges: built.edges,
    settings: template.settings,
    validation_status: 'draft',
  }
}
